import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import {
  addUnitToEveryField,
  convertPhysicalAlignmentToLogical,
  getLabelPaddingsValues,
  withFallbackValues,
  isExperimentOpen,
  castFromInheritIfNeeded,
} from '@wix/editor-elements-common-utils';
import type { PhysicalAlignment } from '@wix/editor-elements-common-utils';
import { getPhysicalInputAlignment } from './utils/utils';
import {
  convertPhysicalInputAlignmentToLogical,
  convertPhysicalInputAlignmentToDirection,
} from '@wix/editor-elements-common-utils/src/commons/inputUtils';

import type {
  RichTextBoxDefinition,
  IRichTextBoxMapperProps,
  RichTextBoxCSSVars,
  RichTextCarmiData,
  IRichTextStateRefs,
  RichTextBoxStyleProperties,
} from '../RichTextBox.types';
import {
  RICH_TEXT_BOX_NAMESPACE,
  TranslationKeys,
  SHOW_TOOLTIPS_EXPERIMENT,
} from '../constants';

const getToolbarPosition = (
  toolbarPosition: IRichTextBoxMapperProps['toolbarPosition'],
  isMobileView: boolean,
): IRichTextBoxMapperProps['toolbarPosition'] => {
  if (isMobileView) {
    return 'bottom';
  }

  return toolbarPosition;
};

const getWithFallbackValues = () => {
  return withFallbackValues([
    {
      sourceNamespace: 'compProps',
      targetNamespace: 'styleProperties',
      fields: [{ source: 'textAlignment', target: 'align' }],
      enhancer: (physicalAlignment: PhysicalAlignment) =>
        convertPhysicalAlignmentToLogical(physicalAlignment),
    },
    {
      sourceNamespace: 'compProps',
      targetNamespace: 'compData',
      fields: [{ source: 'textAlignment', target: 'labelDirection' }],
      enhancer: (alignment: PhysicalAlignment) => {
        const labelDirection =
          convertPhysicalInputAlignmentToDirection(alignment);
        return labelDirection;
      },
    },
    {
      sourceNamespace: 'compProps',
      targetNamespace: 'styleProperties',
      fields: [{ source: 'textAlignment', target: 'labelAlign' }],
      enhancer: (alignment: PhysicalAlignment) => {
        const labelAlign = convertPhysicalInputAlignmentToLogical(alignment);
        return labelAlign;
      },
    },
  ]);
};

export const props = withCompInfo<
  IRichTextBoxMapperProps,
  RichTextBoxDefinition
>()(
  [
    'compData',
    'compProps',
    'isMobileView',
    'compId',
    'translate',
    'experiments',
    'styleProperties',
    'directionWithOptInRtlSupport',
  ],
  ({
    compData,
    compProps,
    isMobileView,
    compId,
    translate,
    experiments,
    styleProperties,
    directionWithOptInRtlSupport: siteDirection = 'ltr',
  }) => {
    const translations = Object.fromEntries(
      Object.entries(TranslationKeys).map(([key, value]) => [
        key,
        translate(RICH_TEXT_BOX_NAMESPACE, value.key) || value.fallback,
      ]),
    ) as { [key in keyof typeof TranslationKeys]: string };

    const direction = castFromInheritIfNeeded(
      compData.direction,
      siteDirection,
    );

    const result: IRichTextBoxMapperProps = {
      compId,
      linkModalId: `linkModal_${compId}`,
      isMobileView,
      label: compProps.label,
      required: compProps.required,
      allowLinks: compProps.allowLinks,
      placeholder: compProps.placeholder,
      value: compData.value,
      textAlignment: getPhysicalInputAlignment(
        compData,
        styleProperties as RichTextBoxStyleProperties,
        siteDirection,
      ),
      direction,
      labelDirection: compData.labelDirection || 'inherit',
      toolbarPosition: getToolbarPosition(
        compProps.toolbarPosition,
        isMobileView,
      ),
      readOnly: compProps.readOnly || false,
      isDisabled: compProps.isDisabled || false,
      maxLength: compData.maxLength || undefined,
      translations,
      shouldShowTooltips: isExperimentOpen(
        experiments,
        SHOW_TOOLTIPS_EXPERIMENT,
      ),
    };

    return result;
  },
  [getWithFallbackValues()],
);

export const css = withCompInfo<
  RichTextBoxCSSVars,
  RichTextBoxDefinition,
  RichTextCarmiData
>()(
  ['compData', 'compProps', 'styleProperties', 'directionWithOptInRtlSupport'],
  (
    {
      compData,
      compProps,
      styleProperties,
      directionWithOptInRtlSupport: siteDirection,
    },
    carmiData,
  ) => {
    const { align, labelAlign } = styleProperties as RichTextBoxStyleProperties;
    const textPadding =
      compProps.textPadding && compProps.textPadding > 0
        ? compProps.textPadding
        : 0;
    const borderRadius = styleProperties.rd.split(' ');
    const borderWidth = styleProperties.brw.includes('px')
      ? styleProperties.brw
      : `${styleProperties.brw}px`;

    const labelAlignCastFromInherit = castFromInheritIfNeeded(
      labelAlign,
      align,
    );
    const { direction = 'inherit', inputDirection = 'inherit' } = compData;
    const realDirection = castFromInheritIfNeeded(direction, siteDirection);

    return {
      '--fontSize': `${carmiData.font.size}px`,
      '--text-align': align,
      '--label-align': labelAlignCastFromInherit,
      '--input-direction': inputDirection || 'inherit',
      '--input-align': styleProperties.inputAlign || 'inherit',
      '--labelMarginBottom': `${compProps.labelMargin}px`,
      '--textPadding': `${textPadding}px`,
      '--textPaddingTop': `${textPadding / 2}px`,
      '--toolbar-top-radius': `${borderRadius[0]} ${borderRadius[1]} 0 0`,
      '--toolbar-bottom-radius': `0 0 ${borderRadius[2]} ${borderRadius[3]}`,
      '--toolbar-scroller-rotation':
        realDirection === 'rtl' ? '180deg' : '0deg',
      '--brw': borderWidth,
      ...addUnitToEveryField(
        getLabelPaddingsValues({
          labelPadding: compProps.labelPadding,
          align: labelAlignCastFromInherit,
        }),
      ),
    };
  },
  [getWithFallbackValues()],
);

export const stateRefs = withStateRefsValues<keyof IRichTextStateRefs>([
  'setSiteScrollingBlocked',
  'enableCyclicTabbing',
  'disableCyclicTabbing',
  'scopedClassName',
]);

export default createComponentMapperModel({ props, css, stateRefs });
